// MSB(마스터 스토리보드) V2 모션 레이어 — PersonaLayer · 모션 훅 · 패턴 · 키워드 강조.
// 원본 프로젝트의 src/motion/ 은 인수인계에 미포함 → 프레임워크 문서 스펙(§3-10·§3-11·§8-3·§8-4,
// V2.22-K helper-first·V2.23.1 자동 트리거·V2.25 useMaskReveal 시그니처)에서 재구현.
//
// 하드룰 준수 사항 (§8-0):
//   - 모든 interpolate 양방향 clamp (베지어 외삽 깜빡임 방지) — 훅에 baked-in
//   - 씬 root opacity 금지 — useFade 는 inner wrapper 전용
//   - 수동 <VfxLayer> 호출 금지 — PersonaLayer 가 timing 메타로 자동 트리거
import React from 'react';
import { AbsoluteFill, interpolate, spring } from 'remotion';
import {
  AtmosphereLayer,
  EASING,
  FLASH_CUT,
  PERSONA_CATALOG,
  resolvePersonaColors,
  SPRING,
  STAGGER,
  VFX_CATALOG,
  VfxLayer,
  UnderlineSweep,
  SwipeHighlight,
  BoxSliderHighlight,
  type AtmosphereColors,
  type AtmosphereKey,
  type PersonaId,
  type PatternId,
  type Intensity,
  type VfxIdExt,
} from './tokens';
// 🧰 모션 부품 상자의 시간 함수(2026-09-30 · 표면 2) — 이징 토큰·길이 기본값·소품 스프링을 여기서 빌린다.
//   ★`kit` 분기가 아니면 아래 헬퍼는 종전 산식 그대로다★(interpolate + EASING) — 기존 작업물 재생 화면 불변.
import { DURATION_MS, EASE_ENTER, EASE_ENTER_SOFT, EASE_EXIT, EASE_MOVE, UI_SPRING, lin, springStep } from '../../lib/motion-kit/spring';

// ─── 모션 헬퍼 (helper-first · 양방향 clamp 내장) ──────────────────
// ※ 전부 순수 함수 (interpolate/spring 은 React 훅이 아님) — map 콜백 안에서도 안전.
//   프레임워크 문서(§8-3)의 use* 명명은 하단 별칭으로 제공 (커스텀 씬 codegen 패리티용).

/* ─── 🧰 «모션 부품» 분기(2026-09-30 · 모션 스타일 재편 표면 2 · 정본 지도 §12) ─────────────────
 * 장표 10종의 움직임은 전부 이 파일의 헬퍼를 지난다. 그래서 규율은 **여기 한 곳**에 두고, 레이아웃은
 * 판정값(`motionKit`)을 `MotionKitOpt` 로 넘기기만 한다(좌표·크기 산식은 그대로).
 *   ① 이징 = 토큰(`EASE_ENTER` · 튀어나감 없음) — linear·back·elastic·bounce 0.
 *   ② 길이 = `DURATION_MS.enter`(500ms) 를 fps 로 프레임 환산 — 400~700ms 범위 안 · 단일 트윈 1000ms 초과 0.
 *   ③ 스프링은 소품(체크 표시·번호 원·✕✓ 표)에만 `UI_SPRING`(f 2.4Hz · ζ 0.42) — 큰 글자는 오버슈트 없음.
 *   ④ 흰 플래시 0 — `flashCut` 의 kit 분기는 번쩍임 없이 부드럽게 들어온다.
 * ★kit 이 아니면 종전 값과 1비트도 다르지 않다★ — 테스트가 «미지정 == kit:false == 종전 산식»을 잰다. */

export interface MotionKitOpt {
  /** true = 부품 상자 규율. 미지정/false = 종전. */
  kit?: boolean;
  /** ms 기본값을 프레임으로 바꾸는 기준. 미지정 30. */
  fps?: number;
}

/** ms → 프레임(최소 1). */
export const kitFrames = (ms: number, fps = 30): number => Math.max(1, Math.round((ms / 1000) * (fps > 0 ? fps : 30)));

/** 부유(`sustainedFloat`)의 «착지 시각»을 세는 진입 길이(프레임) — kit 이면 max(토큰 500ms, 종전 값).
 *  ★종전보다 일찍 뜨지 않는다★(2026-09-30 2차) — 부유 위상이 종전과 같아야 완성 프레임이 종전과 **픽셀까지** 같다
 *  (1차에선 kit 이 15프레임에 뜨기 시작해 부유가 1px 안쪽으로 어긋났다). kit 진입(15f)이 먼저 끝나면 그 사이는 정지. */
export const motionEnterFrames = (opt: MotionKitOpt | undefined, fallback: number): number =>
  opt?.kit ? Math.max(kitFrames(DURATION_MS.enter, opt.fps), fallback) : fallback;

/**
 * 🧷 소품 스프링 «톡» — 체크 표시·번호 원·✕✓ 표가 0.6 배에서 1 배로 착지한다(`UI_SPRING`).
 *   착지(잔차 < 1e-4 · 52px 소품에서 0.005px 아래)하면 **정확히 1** 을 돌려준다 — 완성 프레임의 HTML 이 종전과
 *   같아야 «좌표 불변» 검사가 성립한다. 호출부는 1 이면 transform 을 아예 안 단다. 1.5초(45프레임)면 착지한다.
 */
const PROP_POP_SETTLED_EPS = 1e-4;
export const propPop = (f: number, opts: { enterAt?: number; fps?: number; from?: number } = {}): number => {
  const { enterAt = 0, fps = 30, from = 0.6 } = opts;
  if (f < enterAt) return from;
  const s = springStep((f - enterAt) / (fps > 0 ? fps : 30), UI_SPRING.stiffness, UI_SPRING.damping, UI_SPRING.mass);
  return Math.abs(s - 1) < PROP_POP_SETTLED_EPS ? 1 : from + (1 - from) * s;
};

/* ─── 👀 «눈에 보이는» 부품(2026-09-30 2차 · 회장님 «1번으로 해» — 부모 세션 기록) ───────────────────
 * 전부 **등장 구간에만 존재하고 끝나면 종전 노드로 돌아간다** — 그래서 완성 프레임의 HTML·픽셀은 종전과 같다.
 * 자리·크기는 건드리지 않는다: 어절 조각은 폭이 원문과 같은 inline-block(띄어쓰기에서만 접히는 keep-all 이라 줄바꿈 동일),
 * 숫자 카운트업은 끝값 원문을 숨겨 깔아 폭을 잡고 그 위에 세는 숫자를 얹는다. kit 이 아니면 children 을 그대로 돌려준다. */

/** 어절 등장 — 어절 사이 간격(ms) 상한과 한 문장 전체 퍼짐 상한(ms). 부품 상자 «글자 진입 ≤700ms». */
const WORD_GAP_MS = 110;
const WORD_SPREAD_MAX_MS = 700;
/** 어절 하나가 켜지는 길이 = 진입 토큰(500ms · 부품 상자 «글자 진입 500ms»). */
const WORD_FADE_MS = DURATION_MS.enter;

const _wordTokens = (text: string): string[] => String(text ?? '').split(/(\s+)/).filter((t) => t !== '');
const _isSpace = (t: string) => /^\s+$/.test(t);

/** 어절 등장이 끝나는 프레임(이 프레임부터 원문 노드). 어절 0개면 `at`. */
export const wordsDoneAt = (text: string, at: number, fps = 30): number => {
  const n = _wordTokens(text).filter((t) => !_isSpace(t)).length;
  if (n === 0) return at;
  const gap = Math.min(WORD_GAP_MS, WORD_SPREAD_MAX_MS / Math.max(1, n - 1));
  return at + Math.ceil((((n - 1) * gap + WORD_FADE_MS) / 1000) * fps);
};

/**
 * ① 어절 등장 — 어절이 하나씩 아래에서 살짝 올라오며 켜진다(0.5em · EASE_ENTER · 오버슈트 없음).
 *   끝나면 `children`(종전 `KeywordText` 등)을 그대로 돌려준다.
 */
export const KitWords: React.FC<{ kit?: boolean; fps?: number; f: number; at: number; text: string; children: React.ReactNode }> = ({ kit, fps = 30, f, at, text, children }) => {
  if (!kit || f >= wordsDoneAt(text, at, fps)) return <>{children}</>;
  const toks = _wordTokens(text);
  const n = toks.filter((t) => !_isSpace(t)).length;
  const gapF = (Math.min(WORD_GAP_MS, WORD_SPREAD_MAX_MS / Math.max(1, n - 1)) / 1000) * fps;
  const fadeF = (WORD_FADE_MS / 1000) * fps;
  let wi = -1;
  return (
    <>
      {toks.map((tok, i) => {
        if (_isSpace(tok)) return tok; // 띄어쓰기·줄바꿈은 원문 그대로(pre-line 줄바꿈 보존)
        wi++;
        const p = EASE_ENTER(lin(f, at + wi * gapF, at + wi * gapF + fadeF));
        return <span key={i} style={{ display: 'inline-block', opacity: p, transform: `translateY(${(0.5 * (1 - p)).toFixed(4)}em)` }}>{tok}</span>;
      })}
    </>
  );
};

/** ③ 카운트업 길이(ms) — 단일 트윈 상한(1000ms)까지 쓴다(숫자는 세는 과정이 곧 내용이다). */
const COUNT_MS = DURATION_MS.maxTween;

/** 숫자 부분 찾기 — 첫 숫자 덩이(콤마 묶음·소수 허용). 없으면 null. */
export const parseCountable = (text: string): { prefix: string; num: number; decimals: number; comma: boolean; suffix: string } | null => {
  const s = String(text ?? '');
  const m = /(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?/.exec(s);
  if (!m) return null;
  const intPart = m[1];
  const dec = m[2] ?? '';
  return {
    prefix: s.slice(0, m.index),
    num: Number(intPart.replace(/,/g, '') + dec),
    decimals: dec ? dec.length - 1 : 0,
    comma: intPart.includes(','),
    suffix: s.slice(m.index + m[0].length),
  };
};

/** `p`(0~1) 지점의 카운트업 문자열. p≥1 이면 **원문 그대로**. */
export const countUpText = (text: string, p: number): string => {
  const c = parseCountable(text);
  if (!c || p >= 1) return text;
  let s = (c.num * Math.max(0, p)).toFixed(c.decimals);
  if (c.comma) {
    const [i, d] = s.split('.');
    s = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (d !== undefined ? `.${d}` : '');
  }
  return c.prefix + s + c.suffix;
};

/** ③ 숫자 카운트업 — 0→값(숫자부만 · 한글 단위 고정). 폭은 끝값 원문을 숨겨 깔아 고정한다(흔들림·밀림 0). */
export const KitCount: React.FC<{ kit?: boolean; fps?: number; f: number; at: number; text: string }> = ({ kit, fps = 30, f, at, text }) => {
  const end = at + kitFrames(COUNT_MS, fps);
  if (!kit || f >= end || !parseCountable(text)) return <>{text}</>;
  const shown = countUpText(text, EASE_ENTER_SOFT(lin(f, at, end)));
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      {/* 끝값을 투명하게 깔아 폭을 고정한다 — ★visibility:hidden 이 아니라 opacity:0★. 브라우저 내보내기 렌더러
          (@remotion/web-renderer)는 visibility 를 무시해 끝값이 카운트 숫자와 겹쳐 그려졌다(09-30 스틸 실측 «99일»). */}
      <span aria-hidden style={{ opacity: 0 }}>{text}</span>
      <span style={{ position: 'absolute', left: 0, right: 0, top: 0, textAlign: 'center', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{shown}</span>
    </span>
  );
};

/** ② 소품 «딸깍» — 체크·✕✓ 글리프가 0.3 배에서 `UI_SPRING` 으로 찍힌다. 착지하면 원문 글리프. */
export const KitPop: React.FC<{ kit?: boolean; fps?: number; f: number; at: number; children: React.ReactNode }> = ({ kit, fps = 30, f, at, children }) => {
  if (!kit) return <>{children}</>;
  const sc = propPop(f, { enterAt: at, fps, from: 0.3 });
  if (sc === 1) return <>{children}</>;
  return <span style={{ display: 'inline-block', opacity: f < at ? 0 : 1, transform: `scale(${sc})` }}>{children}</span>;
};

/**
 * 🖱 «모션 브리핑» 커서 — 체크가 켜지는 순간(`at`) 그 칸을 누른다. 0.4초 전 오른쪽 아래 150px 에서 다가와(EASE_MOVE · 궤적이 보이게) 누르고(0.12초 눌림),
 *   크기 = 화면 높이의 약 6%(64px · 기준 쇼릴 4~5% 이상 — 42px 은 체크 칸보다 작아 안 보였다 · 09-30 부모 관찰).
 *   0.15초 뒤 빠진다(EASE_EXIT · 0.25초). 창 밖 프레임에는 DOM 이 없다(완성 프레임 불변). 좌표는 부모(칸이 든 줄) 기준 — 줄바꿈이 달라도 칸에 붙는다.
 *   화살표는 SVG(이모지 아님) · 끝점 = 부모의 (x, 50%).
 */
export const KitCursor: React.FC<{ f: number; fps?: number; at: number; x: number; px: (v: number) => number }> = ({ f, fps = 30, at, x, px }) => {
  const t = (f - at) / fps;
  if (t < -0.4 || t > 0.4) return null;
  const arrive = EASE_MOVE(lin(t, -0.4, 0));
  const leave = EASE_EXIT(lin(t, 0.15, 0.4));
  const dx = px(150) * (1 - arrive) + px(36) * leave;
  const dy = px(100) * (1 - arrive) + px(24) * leave;
  const opacity = Math.min(lin(t, -0.4, -0.3), 1 - leave);
  const press = t >= 0 && t < 0.12 ? 0.86 : 1;
  const w = px(46), h = px(64);
  // ★이동·눌림은 바깥 div 에 건다★ — 브라우저 내보내기 렌더러(web-renderer)는 svg 요소 자체의 transform·overflow 를 무시해
  //   화살표 끝 조각만 제자리에 그려졌다(09-30 스틸 실측). svg 는 상자 안에 고정 크기로만 둔다.
  return (
    <div aria-hidden style={{ position: 'absolute', left: x - px(2), top: `calc(50% - ${px(2)}px)`, width: w, height: h, pointerEvents: 'none',
      opacity, transform: `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px) scale(${press})`, transformOrigin: `${px(2)}px ${px(2)}px`,
      filter: `drop-shadow(0 ${px(3)}px ${px(6)}px rgba(17,24,39,0.28))` }}>
      <svg width={w} height={h} viewBox="0 0 30 42" style={{ display: 'block' }}>
        <path d="M2 2 L2 34 L10 27 L16 40 L22 37 L16 25 L27 25 Z" fill="#111827" stroke="#FFFFFF" strokeWidth={2.2} strokeLinejoin="round" />
      </svg>
    </div>
  );
};

/** ④ 장표 전환 색 쓸림 — 아래→위 0.18s(EASE_ENTER) 로 장표가 드러나고, 경계에 액센트 띠가 따라 올라가 0.3s 에 사라진다. 흰 플래시 0. */
export const slideWipe = (f: number, fps = 30): { p: number; active: boolean; bandOpacity: number } => {
  const d = kitFrames(180, fps);
  const p = EASE_ENTER(lin(f, 0, d));
  const bandOpacity = 1 - lin(f, d, kitFrames(300, fps));
  return { p, active: p < 1 || bandOpacity > 0, bandOpacity };
};

/** ⑤ 진행 막대 — 단계 진입마다 채워지고(EASE_MOVE), 마지막 단계 착지 500ms 뒤 300ms 동안 사라진다(남기지 않는다). */
export const stepProgress = (f: number, enters: number[], fps = 30): { fill: number; opacity: number; active: boolean } => {
  if (enters.length === 0) return { fill: 0, opacity: 0, active: false };
  const d = kitFrames(DURATION_MS.enter, fps);
  const fill = enters.reduce((a, e) => a + EASE_MOVE(lin(f, e, e + d)), 0) / enters.length;
  const last = enters[enters.length - 1] + d;
  const opacity = 1 - lin(f, last + kitFrames(500, fps), last + kitFrames(800, fps));
  return { fill, opacity, active: f >= enters[0] && opacity > 0 };
};

/** opacity 0→1 페이드. ⚠️ 씬 root(AbsoluteFill background 컨테이너)에 사용 금지 — inner wrapper 전용. */
export const fadeIn =(f: number, opts: { enterAt?: number; duration?: number } & MotionKitOpt = {}): number => {
  const { enterAt = 0, duration = 20 } = opts;
  if (opts.kit) return EASE_ENTER_SOFT(lin(f, enterAt, enterAt + kitFrames(DURATION_MS.enter, opts.fps)));
  return interpolate(f, [enterAt, enterAt + duration], [0, 1], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: EASING.easeOut,
  });
};

/** 방향 슬라이드 진입. 반환값을 style 에 스프레드: { opacity, transform } */
export const slideIn = (
  f: number,
  opts: { enterAt?: number; duration?: number; direction?: 'up' | 'down' | 'left' | 'right'; offset?: number } & MotionKitOpt = {},
): { opacity: number; transform: string } => {
  const { enterAt = 0, duration = 24, direction = 'up', offset = 40 } = opts;
  // 🧰 kit — 진입 토큰(0.05,0.7,0.1,1)·길이 500ms. 호출부의 duration 은 종전 경로에서만 쓴다(길이는 토큰이 정한다).
  const p = opts.kit
    ? EASE_ENTER(lin(f, enterAt, enterAt + kitFrames(DURATION_MS.enter, opts.fps)))
    : interpolate(f, [enterAt, enterAt + duration], [0, 1], {
        extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: EASING.snap,
      });
  const d = offset * (1 - p);
  const transform =
    direction === 'up' ? `translateY(${d}px)` :
    direction === 'down' ? `translateY(${-d}px)` :
    direction === 'left' ? `translateX(${d}px)` : `translateX(${-d}px)`;
  return { opacity: p, transform };
};

/** 리스트 순차 진입 — index 별 enterAt 오프셋 + 슬라이드업. STAGGER 토큰 lookup. */
export const staggerReveal = (
  f: number,
  opts: { index: number; baseFrame?: number; staggerFrames?: number; duration?: number; offset?: number } & MotionKitOpt,
): { opacity: number; transform: string } => {
  const { index, baseFrame = 0, staggerFrames = STAGGER.normal, duration = 20, offset = 28 } = opts;
  // 🧰 kit — 누적 표시는 slideIn 이 이미 그렇다(들어온 항목은 남는다). 이징·길이만 토큰으로.
  return slideIn(f, { enterAt: baseFrame + index * staggerFrames, duration, direction: 'up', offset, kit: opts.kit, fps: opts.fps });
};

/** 타이틀 마스크 리빌 (V2.25 정정 시그니처). 사용:
 *   const reveal = maskReveal(f, { enterFrame: 8, fps, lineHeight: 120 });
 *   <div style={{ overflow: 'hidden' }}><div style={{ transform: `translateY(${reveal.y}px)` }}>타이틀</div></div> */
export const maskReveal = (
  f: number,
  opts: { enterFrame?: number; fps?: number; lineHeight: number },
): { y: number; done: boolean } => {
  const { enterFrame = 0, fps = 30, lineHeight } = opts;
  const p = spring({ frame: Math.max(0, f - enterFrame), fps, config: SPRING.reveal });
  return { y: lineHeight * (1 - p), done: p > 0.999 };
};

/** 씬 진입 화이트 플래시 + 씬 페이드 (FLASH_CUT 키프레임). 반환: { flashOpacity, sceneProgress } */
export const flashCut = (f: number, opt?: MotionKitOpt): { flashOpacity: number; sceneProgress: number } => {
  // 🧰 kit — 흰 플래시 0(GNWORKS r-no-flash · «1초 창 휘도 급변 ≤3 · 흰 플래시 0» 검사와 충돌). 씬은 전환 길이(800ms)로 부드럽게.
  if (opt?.kit) return { flashOpacity: 0, sceneProgress: EASE_ENTER_SOFT(lin(f, 0, kitFrames(DURATION_MS.transition, opt.fps))) };
  const [s0, s1, s2] = FLASH_CUT.flashFrames;
  const flashOpacity = interpolate(f, [s0, s1, s2], [1, 1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const sceneProgress = interpolate(f, [FLASH_CUT.sceneFrames[0], FLASH_CUT.sceneFrames[1]], [0, 1], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: EASING.easeOut,
  });
  return { flashOpacity, sceneProgress };
};

// §8-3 문서 패리티 별칭 — 커스텀 씬 코드(P2 escape hatch)가 useFade/useSlide 표기를 쓰더라도 동작.
// (순수 함수이므로 훅 규칙 무관 — 단, 컴포넌트 최상위에서 호출하는 관례 유지 권장)
/* ─── 🎞 지속 모션(sustained) — 등장이 끝난 뒤에도 화면이 살아 있게 한다 ───────────────
 * ★왜 필요한가(2026-09-16 실측)★: 장표 10종 중 **모션 타이포 하나만** 등장 뒤에도 움직였고
 *   나머지 9종은 «미끄러져 들어온 뒤 완전히 멈춘 그림»이었다(지속 모션 호출 0). 인포그래픽을
 *   «모션 그래픽»이라 부르려면 정지 구간이 없어야 한다(회장님 09-15 지시).
 * ★읽기를 방해하지 않는 것이 제약★ — 장표는 읽는 화면이다(W1 «읽는 장표에 글자 바운스는 소음»).
 *   그래서 모션 타이포의 글자별 바운스·물결을 그대로 옮기지 않고, **전체를 아주 천천히 밀고
 *   덩어리를 미세하게 띄우는** 두 가지만 쓴다. 눈은 살아 있다고 느끼되 글자는 제자리로 읽힌다.
 * ★프레임만의 함수★ — Lambda 가 프레임 구간을 워커로 쪼개 렌더하므로 난수·상태를 쓰면 청크
 *   경계에서 튄다(tokens.tsx P2-4 계약과 같은 이유).
 */

/** 카메라 밀어들어가기 — 8초에 걸쳐 1.0 → `to`. 모션 타이포(1.06)보다 약하다: 장표는 글자가 많아
 *  같은 세기로 밀면 가장자리 항목이 화면 밖으로 밀려나 보인다. */
export const sustainedPushIn = (f: number, to = 1.035, frames = 240): number =>
  interpolate(f, [0, frames], [1, to], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

/** 덩어리(카드·패널) 미세 부유 — 항목마다 위상을 달리해 «같이 흔들리는» 느낌을 피한다.
 *  ★등장이 끝난 뒤에만 발동★ — 등장 애니(slideIn)와 겹치면 착지가 흔들려 보인다.
 *  진폭은 360 기준 좌표로 받는다(호출부의 `s.px` 로 해상도에 맞춰 스케일). */
export const sustainedFloat = (f: number, index: number, settledAt = 0, amplitudePx = 3): number => {
  if (f < settledAt) return 0;
  return Math.sin((f - settledAt) * 0.03 + index * 1.7) * amplitudePx;
};

export const useFade = fadeIn;
export const useSlide = slideIn;
export const useStaggerReveal = staggerReveal;
export const useMaskReveal = maskReveal;
export const useFlashCut = flashCut;

// ─── 페르소나 패턴 레이어 (§3-10 pattern — 정적 텍스처, ambient 가 모션 담당) ──
// ★CSS backgroundImage 그라디언트 타일은 @remotion/web-renderer 렌더 캡처에서 안 칠해진다
//   (2026-07-15 렌더 스틸 실측: dotGrid 비검정 픽셀 0.00% — 프리뷰 OK≠렌더 OK). PersonaLayer
//   배경(SVG radialGradient 교체)과 동일 근본 → SVG <pattern>/지오메트리 등가로 전환.
//   타일 기하·수치는 구 CSS 스펙 그대로 (dotGrid 36px·lineGrid 44px·hexGrid 64×56 링 r19·
//   diagonalLines 45deg/28px·circuit 80px+교차점 도트 r2·topo (30%,70%) 동심 타원 48px 간격).

const _PATTERN_OPACITY: Record<Intensity, number> = { sm: 0.05, md: 0.08, lg: 0.12 };

// topo_lines: repeating-radial-gradient(ellipse 120% 90% at 30% 70%) 등가 — (576,756) 중심
// 동심 타원 링을 최원점 코너(rx≈2240)까지. ry/rx = (0.9·1080)/(1.2·1920).
const _TOPO_ASPECT = (0.9 * 1080) / (1.2 * 1920);
const _TOPO_RX = Array.from({ length: 47 }, (_, n) => 47 + 48 * n);

const _patternTile = (type: Exclude<PatternId, 'topo_lines'>, id: string, col: string): React.ReactElement => {
  switch (type) {
    case 'dotGrid':
      return (
        <pattern id={id} width={36} height={36} patternUnits="userSpaceOnUse">
          <circle cx={18} cy={18} r={1} fill={col} />
        </pattern>
      );
    case 'lineGrid':
      return (
        <pattern id={id} width={44} height={44} patternUnits="userSpaceOnUse">
          <rect width={44} height={1} fill={col} />
          <rect width={1} height={44} fill={col} />
        </pattern>
      );
    case 'hexGrid':
      // 육각 근사 — 반복 링 (구 CSS 근사와 동일 의도)
      return (
        <pattern id={id} width={64} height={56} patternUnits="userSpaceOnUse">
          <circle cx={32} cy={28} r={19} fill="none" stroke={col} strokeWidth={1.5} />
        </pattern>
      );
    case 'diagonalLines':
      // 45deg 1px 라인 · 수직 간격 28px — 가로라인 타일을 rotate(45)
      return (
        <pattern id={id} width={28} height={28} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width={28} height={1} fill={col} />
        </pattern>
      );
    case 'circuit_lines':
      return (
        <pattern id={id} width={80} height={80} patternUnits="userSpaceOnUse">
          <rect y={40} width={80} height={1} fill={col} />
          <rect x={40} width={1} height={80} fill={col} />
          <circle cx={40} cy={40} r={2} fill={col} />
        </pattern>
      );
  }
};

/** 페르소나 pattern 스펙 → 정적 텍스처 레이어. PersonaLayer 가 자동 합성 — 직접 호출 지양. */
export const PatternLayer: React.FC<{ type: PatternId; intensity: Intensity; colors: AtmosphereColors }> = ({ type, intensity, colors }) => {
  const col = colors.accent;
  // pattern id = type+색 결정론 파생 — 동일 id ⇒ 동일 정의라 동시 마운트 충돌 무해 (PersonaLayer 관용구)
  const id = `msb-pat-${type}-${col.replace(/[^a-zA-Z0-9]/g, '')}`;
  return (
    <AbsoluteFill style={{ opacity: _PATTERN_OPACITY[intensity], pointerEvents: 'none' }}>
      <svg width="100%" height="100%" viewBox="0 0 1920 1080" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0 }}>
        {type === 'topo_lines' ? (
          _TOPO_RX.map((rx) => (
            <ellipse key={rx} cx={576} cy={756} rx={rx} ry={rx * _TOPO_ASPECT} fill="none" stroke={col} strokeWidth={1.5} />
          ))
        ) : (
          <>
            <defs>{_patternTile(type, id, col)}</defs>
            <rect width={1920} height={1080} fill={`url(#${id})`} />
          </>
        )}
      </svg>
    </AbsoluteFill>
  );
};

// ─── PersonaLayer — 페르소나 자동 합성 (§3-10 / V2.23.1) ────────────
// 한 줄로 bg + ambient + pattern + 권장 vfx 자동 발동:
//   <PersonaLayer persona="tension" f={f} duration={SD} />
// timing 메타: entry → frame 0 / climax → duration/2 앵커 정렬 / persistent → 씬 전체.
// MSB vfxTriggers(특수 타이밍) 는 prop 으로 전달 시 추가 발동.

/** VFX 내부 임팩트 앵커(컴포넌트가 45f 기준으로 설계됨 — ChromaticAberration/StrobeFlash 등).
 *  ⚠️ 알려진 한계: duration < 90f 씬은 start 가 0 으로 클램프되어 앵커가 duration/2 에 못 미치고
 *  45f(1.5s)에 발동한다 — 45f 미만 초단씬은 피크가 씬 밖일 수 있음. 원본 컴포넌트 앵커는 이식
 *  불변 원칙이라 유지 (짧은 씬 재스케일은 P2 후보). */
const _VFX_INTERNAL_ANCHOR = 45;

export interface PersonaLayerProps {
  persona: PersonaId;
  f: number;
  /** 씬 길이(frames). 전달 시 페르소나 권장 vfx 자동 트리거 (climax 타이밍 계산에 필요) */
  duration?: number;
  /** 색 override — 미전달 시 resolvePersonaColors(persona). §8-4: 두 곳에 동일 페르소나 ID 전달 필수 */
  colors?: AtmosphereColors;
  /** MSB SceneSpec.vfxTriggers — 특수 타이밍 추가 발동 */
  vfxTriggers?: ReadonlyArray<{ vfxId: VfxIdExt; atFrame: number }>;
  /** 씬별 ambient override (components.atmosphere.bgComponents 배선) — 있으면 페르소나 기본 ambient 대신 사용.
   *  키는 buildMsbScenes 가 ATMOSPHERE_A 로 검증·상한(2)해서 넘긴다. pattern·autoVfx 는 페르소나 유지. */
  ambientOverride?: readonly AtmosphereKey[];
}

/** 미지 페르소나 방어 폴백 — 렌더러는 절대 크래시 금지 (msb-director 가 error 승격으로 걸러주지만
 *  구 산출물·수동 데이터 유입 대비 이중 방어). */
export const normalizePersonaId = (id: string): PersonaId =>
  // own-property 체크 — 'constructor' 류 prototype 키 방어 (in 은 상속 키도 통과).
  (Object.prototype.hasOwnProperty.call(PERSONA_CATALOG, id) ? id : 'focusing') as PersonaId;

export const PersonaLayer: React.FC<PersonaLayerProps> = ({ persona, f, duration, colors, vfxTriggers, ambientOverride }) => {
  const pid = normalizePersonaId(persona);
  const spec = PERSONA_CATALOG[pid];
  const C = colors ?? resolvePersonaColors(pid);
  // ★배경 그라디언트 = SVG radialGradient (2026-07-15 렌더 스틸 실측): CSS radial-gradient 는
  //   @remotion/web-renderer 렌더에서 안 칠해져 최종 mp4 의 M 씬 배경이 검정으로 빠진다
  //   (persona 'opening' 류 밝은 배경+어두운 텍스트 조합은 텍스트까지 묻혀 사실상 검정 화면 —
  //   프리뷰 OK≠렌더 OK). SVG 는 같은 스틸에서 렌더 증명. 기하는 원본 CSS
  //   `ellipse 130% 110% at 50% 38%, panel 0%, bg 68%` 근사 + 베이스 솔리드(C.bg)로 최악에도 검정 방지.
  //   gradient id 는 색 기반 결정론(동시 마운트 시 동색=동일 정의라 충돌 무해).
  const bgGradId = `msb-persona-bg-${C.panel.replace(/[^a-zA-Z0-9]/g, '')}-${C.bg.replace(/[^a-zA-Z0-9]/g, '')}`;

  // 권장 vfx 자동 발동 — video_scale_wiggle 은 wrapper 패턴이라 제외 (§8-3)
  const autoVfx = (duration ? spec.vfx : []).filter((id) => id !== 'video_scale_wiggle');

  return (
    <AbsoluteFill style={{ background: C.bg }}>
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }} preserveAspectRatio="none">
        <defs>
          <radialGradient id={bgGradId} cx="50%" cy="38%" r="120%">
            <stop offset="0%" stopColor={C.panel} />
            <stop offset="68%" stopColor={C.bg} />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill={`url(#${bgGradId})`} />
      </svg>
      {(ambientOverride?.length ? ambientOverride : [spec.ambient]).map((amb) => (
        <AtmosphereLayer key={amb} id={amb} f={f} colors={C} scheme="accent" />
      ))}
      {spec.pattern ? <PatternLayer type={spec.pattern.type} intensity={spec.pattern.intensity} colors={C} /> : null}
      {autoVfx.map((id) => {
        const meta = VFX_CATALOG[id as keyof typeof VFX_CATALOG];
        if (!meta) return null;
        if (meta.timing === 'entry') return <VfxLayer key={id} id={id as keyof typeof VFX_CATALOG} f={f} />;
        if (meta.timing === 'persistent') return <VfxLayer key={id} id={id as keyof typeof VFX_CATALOG} f={f} />;
        // climax — 컴포넌트 내부 45f 앵커가 duration/2 에 정렬되도록 오프셋 (짧은 씬은 0 클램프)
        const start = Math.max(0, Math.round((duration ?? 0) / 2) - _VFX_INTERNAL_ANCHOR);
        return f >= start ? <VfxLayer key={id} id={id as keyof typeof VFX_CATALOG} f={f - start} /> : null;
      })}
      {(vfxTriggers ?? []).map(({ vfxId, atFrame }) =>
        vfxId !== 'video_scale_wiggle' && f >= atFrame
          ? <VfxLayer key={`${vfxId}-${atFrame}`} id={vfxId as keyof typeof VFX_CATALOG} f={f - atFrame} />
          : null,
      )}
    </AbsoluteFill>
  );
};

// ─── 키워드 자동 강조 (V2.23.1-B — layout keyword prop 의 구현) ──────

export type KeywordEmphasisId = 'underline_sweep' | 'swipe_highlight' | 'box_slider_highlight';

/** 페르소나별 기본 keyword emphasis (V2.23.1: analytical·compliance → box / tension·critical·impact·resolution → swipe / 그 외 → underline) */
export const personaToKeywordEmphasis = (persona?: PersonaId): KeywordEmphasisId => {
  if (persona === 'analytical' || persona === 'compliance') return 'box_slider_highlight';
  if (persona === 'tension' || persona === 'critical' || persona === 'impact' || persona === 'resolution') return 'swipe_highlight';
  return 'underline_sweep';
};

/** 텍스트 내 keyword 자동 검출 + emphasis wrapper 적용. 미검출 시 원문 그대로. */
export const KeywordText: React.FC<{
  text: string;
  keyword?: string;
  emphasis?: KeywordEmphasisId;
  colors: AtmosphereColors;
  f: number;
  /** 강조 발동 프레임 오프셋 (기본: 텍스트 진입 완료 후 8f) */
  revealAt?: number;
}> = ({ text, keyword, emphasis = 'underline_sweep', colors, f, revealAt = 8 }) => {
  if (!keyword || !text.includes(keyword)) return <>{text}</>;
  const idx = text.indexOf(keyword);
  const before = text.slice(0, idx);
  const after = text.slice(idx + keyword.length);
  const ef = f - revealAt;
  const wrapped =
    emphasis === 'swipe_highlight' ? <SwipeHighlight f={ef} colors={colors}>{keyword}</SwipeHighlight> :
    emphasis === 'box_slider_highlight' ? <BoxSliderHighlight f={ef} colors={colors}>{keyword}</BoxSliderHighlight> :
    <UnderlineSweep f={ef} colors={colors}>{keyword}</UnderlineSweep>;
  return <>{before}{wrapped}{after}</>;
};
